import React, { useState } from "react";
import dayjs from "dayjs";
import { IconButton, Typography } from "@mui/material";
import { CalendarMonth, SettingsApplications } from "@mui/icons-material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { useGridApiContext } from "@mui/x-data-grid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  POLICY_STATUS_DELETED,
  POLICY_STATUS_NEW,
  POLICY_STATUS_VARIANCE,
} from "../Constants/PolicyTranslationConstants";
import ApplyAdoptionDecisionsDialog from "containers/PolicyEnforcementRules/components/adoptionDecision/ApplyAdoptionDecisionDialog";

const StartDateFragment = ({
  updateRulesAfterEdit,
  handleRemoveInvalidRow,
  editable,
  params,
  policyData,
  variant,
  handleValidate = () => {},
  endExistingRule,
}) => {
  const rowId = params.row.id;
  const apiRef = useGridApiContext();
  const [openApplyAdoptionDecisionDialog, setOpenApplyAdoptionDecisionDialog] =
    useState(false);

  const handleDateChange = (newValue) => {
    let newRow = structuredClone(params.row);
    let formattedDate = newValue ? dayjs(newValue).format("D-MMM-YYYY") : "";
    newRow.RuleFacts.RuleEffectiveDate = formattedDate;
    updateRulesAfterEdit(newRow, params.row);
    handleValidate(params, newValue);
  };

  /**
   *
   * @param {[]} adoptionData PolicyData.lobAdoptionDecisionList
   */
  const handleAdoptionDecisionSave = (adoptionData) => {
    let index = params.row.index;
    const lobAdoptionDecisionList = policyData.lobAdoptionDecisionList || [];
    const HealthPlanId = params.row.RuleFacts?.HealthPlanId || "";
    const globalSelected = adoptionData.find(
      (item) => item.line === "Global" && item.enabled
    );
    const allSelectedExceptGlobal = adoptionData.every(
      (item) => item.line === "Global" || item.enabled
    );
    const allDisabled = adoptionData.every((item) => !item.enabled);
    const editRule = params.row.RuleHeader?.EditRule || "";
    const allSameEffectiveDate = lobAdoptionDecisionList.every((item) => {
      return (
        new Date(item.effectiveDate).toLocaleDateString() ===
        new Date(lobAdoptionDecisionList[0].effectiveDate).toLocaleDateString()
      );
    });

    const selectedGlobalEntry = adoptionData.find(
      (item) => item.line === "Global" && item.enabled
    );

    const fallbackGlobalEffectiveDate =
      lobAdoptionDecisionList.length > 0
        ? dayjs(lobAdoptionDecisionList[0].effectiveDate).format("D-MMM-YYYY")
        : "";

    const globalEffectiveDate =
      globalSelected || allSelectedExceptGlobal
        ? selectedGlobalEntry?.date
          ? dayjs(selectedGlobalEntry.date).format("D-MMM-YYYY")
          : fallbackGlobalEffectiveDate
        : "";
    let lobSelected = [];

    // Check for selected lobs
    adoptionData.map((item) => {
      if (item.line !== "Global" && item.enabled) {
        lobSelected.push({
          LobConfigKey: item.id != null ? String(item.id) : "",
          effectiveDate: dayjs(item.date).format("D-MMM-YYYY") || "",
          endDate: item.endDate || "",
        });
      }
    });

    if (
      (globalSelected || allSelectedExceptGlobal) &&
      ![POLICY_STATUS_VARIANCE, POLICY_STATUS_NEW].includes(editRule)
    ) {
      let newRow = structuredClone(params.row);
      newRow.RuleFacts.LobConfigKey = "";
      if (allSameEffectiveDate) {
        newRow.RuleFacts.RuleEffectiveDate = globalEffectiveDate;
      }
      updateRulesAfterEdit(newRow, params.row);
      setOpenApplyAdoptionDecisionDialog(false);
    } else if (
      (globalSelected || allSelectedExceptGlobal) &&
      [POLICY_STATUS_VARIANCE, POLICY_STATUS_NEW].includes(editRule) &&
      HealthPlanId !== "AHCS"
    ) {
      let newRow = structuredClone(params.row);
      newRow.RuleFacts.LobConfigKey = "";
      if (allSameEffectiveDate) {
        newRow.RuleFacts.RuleEffectiveDate = globalEffectiveDate;
      }
      updateRulesAfterEdit(newRow, params.row);
      setOpenApplyAdoptionDecisionDialog(false);
    } else if (
      //immutable rule
      !globalSelected &&
      !allSelectedExceptGlobal &&
      ["EDIT"].includes(editRule)
    ) {
      let oldRow = structuredClone(params.row);
      oldRow.RuleFacts.LobConfigKey = lobSelected;
      endExistingRule(oldRow);
      setOpenApplyAdoptionDecisionDialog(false);
    } else if (
      !allDisabled &&
      !globalSelected &&
      [POLICY_STATUS_VARIANCE, "", POLICY_STATUS_NEW].includes(editRule) &&
      HealthPlanId !== "AHCS"
    ) {
      let newRow = structuredClone(params.row);
      newRow.RuleFacts.LobConfigKey = lobSelected;
      if (newRow.RuleFacts?.RuleEffectiveDate == null) {
        newRow.RuleFacts.RuleEffectiveDate =
          lobSelected[0]?.effectiveDate || "";
      }
      //add effective date to the clob.
      updateRulesAfterEdit(newRow, params.row);
      setOpenApplyAdoptionDecisionDialog(false);
    } else if (
      allDisabled &&
      [POLICY_STATUS_VARIANCE, POLICY_STATUS_NEW].includes(editRule) &&
      HealthPlanId !== "AHCS"
    ) {
      //new rule with no lob selected
      handleRemoveInvalidRow(index);
      setOpenApplyAdoptionDecisionDialog(false);
    }
  };

  const handleEndDateChange = (newValue, index) => {
    let newRow = structuredClone(params.row);
    newRow.RuleFacts.RuleEndDate = newValue
      ? dayjs(newValue).format("D-MMM-YYYY")
      : "";
    updateRulesAfterEdit(newRow, params.row);
    handleValidate(params, newValue);
  };

  // Check if Health Plan has LOBs configured
  const hasLobs =
    Array.isArray(policyData.lobAdoptionDecisionList) &&
    policyData.lobAdoptionDecisionList.length > 0;

  // Check if rule is immutable
  const isImmutable =
    params.row.RuleHeader?.EditRule &&
    ["EDIT"].includes(params.row.RuleHeader.EditRule);

  // Check if rule is deleted
  const isDeleted =
    (params.row.RuleHeader.EditRule ?? "").toUpperCase() ===
    POLICY_STATUS_DELETED;

  return (
    <>
      {!isDeleted && (
        <>
          {/* Health Plan has LOBs: show gear icon, hide date pickers */}
          {hasLobs ? (
            <>
              <IconButton
                aria-label="info"
                disabled={!editable}
                onClick={() => setOpenApplyAdoptionDecisionDialog(true)}
              >
                <SettingsApplications />
              </IconButton>
              <ApplyAdoptionDecisionsDialog
                editable={editable && !isImmutable}
                open={openApplyAdoptionDecisionDialog}
                onClose={() => setOpenApplyAdoptionDecisionDialog(false)}
                policyData={policyData}
                variant={variant}
                handleAdoptionDecisionSave={handleAdoptionDecisionSave}
                row={params.row}
              />
            </>
          ) : (
            // Health Plan has NO LOBs: show date pickers
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {/* Effective Date Picker */}
              <DesktopDatePicker
                disabled={isImmutable ? true : !editable}
                slots={{ openPickerIcon: CalendarMonth }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      width: "151px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "& .MuiInputBase-input": { fontSize: "0.875rem" },
                    },
                    inputProps: {
                      "data-testid": "date-picker",
                    },
                  },
                }}
                value={
                  params.row.RuleFacts?.RuleEffectiveDate
                    ? dayjs(params.row.RuleFacts.RuleEffectiveDate)
                    : null
                }
                onChange={handleDateChange}
              />
              {/* End Date Picker: only for immutable rules */}
              {isImmutable && (
                <>
                  <Typography>to</Typography>
                  <DesktopDatePicker
                    disabled={!editable}
                    slots={{ openPickerIcon: CalendarMonth }}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: "151px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "& .MuiInputBase-input": { fontSize: "0.875rem" },
                        },
                      },
                    }}
                    value={
                      params.row.RuleFacts?.RuleEndDate
                        ? dayjs(params.row.RuleFacts.RuleEndDate)
                        : null
                    }
                    onChange={(newValue) =>
                      handleEndDateChange(newValue, params.row.index)
                    }
                  />
                </>
              )}
            </LocalizationProvider>
          )}
        </>
      )}
    </>
  );
};

export default StartDateFragment;
